/**
 * Assembles a finished (or in-progress) Simulation into an EpisodeData record.
 * Pure module — ids, wall-clock timestamps, and device info are injected by
 * the caller so this works identically in the browser and under Node tests.
 */

import { SAMPLE_RATE_HZ } from "../sim/constants";
import type { Simulation } from "../sim/simulation";
import {
  SCHEMA_VERSION,
  type AppearanceRecord,
  type DeviceInfo,
  type EpisodeData,
  type PlayerType,
} from "./schema";

export interface RecorderContext {
  appearance?: AppearanceRecord | null;
  sessionId: string;
  episodeId: string;
  attemptNumber: number;
  playerType: PlayerType;
  scriptedPlayerName: string | null;
  device: DeviceInfo;
  createdAtIso: string;
  appVersion: string;
  /**
   * What the driver could SEE while driving this, so the episode can be paired
   * with its footage afterwards (ADR-040).
   *
   * Without these two the pairing is manual: a folder of `.webm` files and a
   * folder of episode ids, matched by hand on timestamps. `view` matters as
   * much as the name — a first-person take and a chase take of the same route
   * are completely different footage for identical telemetry.
   */
  playView?: "first" | "third";
  /** Base name of the recorded video, or null if the run was not recorded. */
  videoBaseName?: string | null;
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

export function buildEpisode(sim: Simulation, ctx: RecorderContext): EpisodeData {
  return {
    schema_version: SCHEMA_VERSION,
    appearance: ctx.appearance ? structuredClone(ctx.appearance) : null,
    app_version: ctx.appVersion,
    play_view: ctx.playView ?? "third",
    video_base_name: ctx.videoBaseName ?? null,
    created_at_iso: ctx.createdAtIso,
    session_id: ctx.sessionId,
    episode_id: ctx.episodeId,
    level_id: sim.cfg.level,
    variant: sim.cfg.variant,
    mirror: sim.cfg.mirror ? 1 : 0,
    seed: sim.cfg.seed,
    attempt_number: ctx.attemptNumber,
    is_first_attempt: ctx.attemptNumber === 1,
    player_type: ctx.playerType,
    scripted_player_name: ctx.scriptedPlayerName,
    robot_profile: sim.robotProfile,
    speed_profile: { name: sim.eff.speed_profile, multiplier: sim.eff.multiplier },
    tuning: { ...sim.eff.tuning },
    control_scheme: sim.controlScheme,
    assist: sim.assistMode,
    penalties: { time_s: sim.penaltyTimeS },
    research_validity: sim.researchValidity ? { ...sim.researchValidity } : null,
    mission: sim.level.mission
      ? {
          map_id: sim.level.mission.mapId,
          map_hash: sim.mapHash,
          case_id: sim.caseId,
          route_length_m: round3(sim.level.mission.path.length),
          destination: { ...sim.level.mission.destination },
          sections: sim.level.mission.sections.map((s) => ({
            ...s,
            sStart: round3(s.sStart),
            sEnd: round3(s.sEnd),
          })),
          turns: sim.level.mission.path.turns.map((t) => ({
            s: round3(t.s),
            kind: t.kind,
            exit: t.exit ?? null,
            label: t.label,
          })),
        }
      : null,
    encounters: sim.encounterRecords(),
    score: sim.score ? { ...sim.score } : null,
    // Built at finish(); an in-progress snapshot reports what happened so far.
    report: sim.report ?? sim.reportSoFar(),
    sim: { dt_s: sim.dt, sample_rate_hz: SAMPLE_RATE_HZ },
    device: ctx.device,
    samples: [...sim.samples],
    events: [...sim.events],
    outcome: sim.outcome ?? {
      result: "incomplete",
      passing_moment_t: null,
      committed_side: null,
      collision: false,
      completed: false,
      duration_s: sim.t,
    },
  };
}
